import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { tx } from '@/translations/translations';

const FAQ_ITEMS = [
  {
    q: {
      es: '¿Los tzitziot tienen certificación kosher?',
      en: 'Are your tzitziot kosher certified?',
      he: 'האם לציציות יש תעודת כשרות?',
    },
    a: {
      es: 'Sí, todos nuestros tzitziot cuentan con certificación Kosher Mehadrin. Los hilos son elaborados a mano (Avodat Yad) supervisados por autoridades rabínicas reconocidas.',
      en: 'Yes, all our tzitziot are Kosher Mehadrin certified. Threads are handmade (Avodat Yad) and supervised by recognized rabbinical authorities.',
      he: 'כן, כל הציציות שלנו עם כשרות מהדרין. החוטים נטווים בעבודת יד (עבודת יד) בהשגחת רבנים מוכרים.',
    },
  },
  {
    q: {
      es: '¿Cómo elijo la talla correcta?',
      en: 'How do I choose the right size?',
      he: 'איך בוחרים את המידה הנכונה?',
    },
    a: {
      es: 'Tenemos tallas desde Niño 1-2 hasta Adulto XXL. Dentro de cada producto encontrarás una guía de tallas detallada con medidas en centímetros. Si tienes dudas, escríbenos por WhatsApp y te asesoramos.',
      en: 'We offer sizes from Kids 1-2 to Adult XXL. Inside each product you will find a detailed size guide with measurements in centimeters. If in doubt, message us on WhatsApp and we will help you.',
      he: 'יש לנו מידות מילד 1-2 ועד מבוגר XXL. בכל מוצר תמצאו מדריך מידות מפורט עם מדידות בסנטימטרים. בספק? כתבו לנו בוואטסאפ ונשמח לייעץ.',
    },
  },
  {
    q: {
      es: '¿Cuánto tarda el envío?',
      en: 'How long does shipping take?',
      he: 'כמה זמן לוקח המשלוח?',
    },
    a: {
      es: 'CDMX y Estado de México: 24-48 horas. A partir de 5 productos el envío es gratis a todo México.',
      en: 'CDMX & Estado de México: 24-48 hours. 5+ products ship free nationwide.',
      he: 'מקסיקו סיטי והמדינה: 24-48 שעות. מ-5 מוצרים המשלוח חינם לכל מקסיקו.',
    },
  },
  {
    q: {
      es: '¿Qué diferencia hay entre los materiales?',
      en: "What's the difference between the materials?",
      he: 'מה ההבדל בין החומרים?',
    },
    a: {
      es: 'Lana: la más tradicional y kosher por excelencia, cumple con todas las opiniones halájicas. Algodón: fresca y ligera, ideal para clima cálido. Bambú: ultra suave, antibacterial y eco-friendly. Dry-Fit: tecnología deportiva que absorbe el sudor, perfecta para actividad física.',
      en: 'Wool: the most traditional and preferred kosher option, fulfilling every halachic opinion. Cotton: cool and lightweight, ideal for warm weather. Bamboo: ultra-soft, antibacterial and eco-friendly. Dry-Fit: sport tech that wicks sweat, perfect for physical activity.',
      he: 'צמר: המסורתי והמהודר ביותר, יוצא ידי כל הדעות ההלכתיות. כותנה: קלילה ונעימה, אידיאלית לאקלים חם. במבוק: רך במיוחד, אנטיבקטריאלי וידידותי לסביבה. דריי-פיט: טכנולוגיה ספורטיבית שסופגת זיעה, מושלמת לפעילות גופנית.',
    },
  },
  {
    q: {
      es: '¿Cómo debo lavar mi tzitzit?',
      en: 'How should I wash my tzitzit?',
      he: 'איך לכבס את הציצית?',
    },
    a: {
      es: 'Lavar a mano o en ciclo delicado con agua fría. No usar blanqueador. Secar en plano: extiende la prenda horizontalmente sobre una toalla limpia a la sombra para que mantenga su forma y los hilos no se estiren por el peso del agua. Evita colgar mojado. Los hilos deben mantenerse sueltos al lavar — evita torcer o estirar.',
      en: 'Hand-wash or delicate cycle with cold water. No bleach. Dry flat: lay the garment horizontally on a clean towel in the shade so it keeps its shape and the threads don\'t stretch under the weight of the water. Avoid hanging while wet. Keep threads loose during washing — avoid twisting or pulling.',
      he: 'כביסה ביד או במחזור עדין במים קרים. ללא אקונומיקה. ייבוש בשטוח: הניחו את הבגד אופקית על מגבת נקייה בצל כדי לשמור על צורתו ושהחוטים לא יימתחו ממשקל המים. הימנעו מתלייה כשהבגד רטוב. שמרו על החוטים משוחררים בכביסה — ללא פיתול או מתיחה.',
    },
  },
  {
    q: {
      es: '¿Qué tradiciones de amarre manejan?',
      en: 'What tying traditions do you offer?',
      he: 'אילו מסורות קשירה יש לכם?',
    },
    a: {
      es: 'Ofrecemos amarre Sefaradí, Ashkenazi o sin amarrar (para que tú los amarres o los lleves a tu rabino). También tenemos Kol Haposkim, que cumple con todas las opiniones halájicas.',
      en: 'We offer Sephardic, Ashkenazi, or untied knots (so you can tie them yourself or bring them to your rabbi). We also carry Kol Haposkim, which fulfills all halachic opinions.',
      he: 'אנו מציעים קשירה ספרדית, אשכנזית או ללא קשירה (כדי שתקשרו בעצמכם או אצל הרב שלכם). יש לנו גם כל הפוסקים, היוצא ידי כל הדעות ההלכתיות.',
    },
  },
  {
    q: {
      es: '¿Qué métodos de pago aceptan?',
      en: 'What payment methods do you accept?',
      he: 'אילו אמצעי תשלום אתם מקבלים?',
    },
    a: {
      es: 'Mercado Pago (tarjetas Visa, Mastercard, AMEX), PayPal y transferencia bancaria directa. Todos los pagos son seguros y encriptados.',
      en: 'Mercado Pago (Visa, Mastercard, AMEX), PayPal, and direct bank transfer. All payments are secure and encrypted.',
      he: 'מרקדו פאגו (ויזה, מאסטרקארד, אמקס), PayPal והעברה בנקאית ישירה. כל התשלומים מאובטחים ומוצפנים.',
    },
  },
];

const FAQSection = () => {
  const { language } = useLanguage();
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section id="faq" className="py-12 md:py-20 bg-white" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-cyan-50 text-cyan-700 text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full mb-3">
            <HelpCircle className="h-3.5 w-3.5" />
            FAQ
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
            {tx(language, 'Preguntas Frecuentes', 'Frequently Asked Questions', 'שאלות נפוצות')}
          </h2>
          <p className="text-sm text-slate-500">
            {tx(language, 'Todo lo que necesitas saber sobre nuestros tzitziot', 'Everything you need to know about our tzitziot', 'כל מה שצריך לדעת על הציציות שלנו')}
          </p>
        </div>

        <div className="space-y-2.5">
          {FAQ_ITEMS.map((item, idx) => {
            const isOpen = openIdx === idx;
            return (
              <div
                key={idx}
                className={`rounded-xl border transition-all ${
                  isOpen
                    ? 'border-slate-900 bg-slate-50 shadow-sm'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
                data-testid={`faq-item-${idx}`}
              >
                <button
                  onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                  className="w-full flex items-center justify-between gap-4 px-4 sm:px-5 py-3.5 text-left"
                >
                  <span className={`text-sm sm:text-base font-semibold ${isOpen ? 'text-slate-900' : 'text-slate-700'}`}>
                    {item.q[language] || item.q.en}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 flex-shrink-0 transition-transform duration-300 ${
                      isOpen ? 'rotate-180 text-cyan-600' : 'text-slate-400'
                    }`}
                  />
                </button>
                <div
                  className={`grid transition-all duration-300 ease-out ${
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-4 sm:px-5 pb-4 text-sm text-slate-600 leading-relaxed">
                      {item.a[language] || item.a.en}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-slate-500 mt-8">
          {tx(language, '¿Tu pregunta no está aquí? ', "Don't see your question? ", 'לא מצאתם את השאלה? ')}
          <a
            href="https://wa.me/525543233705?text=%C2%A1Hola%21%20Tengo%20una%20duda%20sobre%20los%20tzitziot%20de%20Mitzva%20Fit."
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-600 hover:text-cyan-700 font-semibold"
          >
            {tx(language, 'Escríbenos por WhatsApp', 'Message us on WhatsApp', 'כתבו לנו בוואטסאפ')}
          </a>
        </p>
      </div>
    </section>
  );
};

export default FAQSection;
